import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useDelete, useGetIdentity, useShow } from "@refinedev/core";
import { useParams, useNavigate } from "react-router-dom";
import ChatBubble from "@mui/icons-material/ChatBubble";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Phone from "@mui/icons-material/Phone";
import Place from "@mui/icons-material/Place";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";

import { CustomButton } from "components";

function checkImage(url: any) {
  const img = new Image();
  img.src = url;
  return img.width !== 0 && img.height !== 0;
}

const EventDetails = () => {
  const navigate = useNavigate();
  const { data: user } = useGetIdentity({
    v3LegacyAuthProviderCompatible: true,
  });
  const { queryResult } = useShow();
  const { mutate } = useDelete();
  const { id } = useParams();

  const { data, isLoading, isError } = queryResult;

  const eventDetails = data?.data ?? {};

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Something went wrong!</div>;
  }

  const isCurrentUser = user.email === eventDetails.creator.email;

  const handleDeleteEvent = () => {
    const response = confirm("Are you sure you want to delete this event?");
    if (response) {
      mutate(
        {
          resource: "events",
          id: id as string,
        },
        {
          onSuccess: () => {
            navigate("/events");
          },
        }
      );
    }
  };

  return (
    <Box
      borderRadius="15px"
      padding="20px"
      bgcolor="#FCFCFC"
      width="fit-content"
    >
      <Typography fontSize={25} fontWeight={700} color="#11142D">
        Event Details
      </Typography>

      <Box
        mt="20px"
        display="flex"
        flexDirection={{ xs: "column", lg: "row" }}
        gap={4}
      >
        <Box flex={1} maxWidth={764}>
          <img
            src={eventDetails.photo}
            alt="leader_details-img"
            height={546}
            style={{ objectFit: "cover", borderRadius: "10px" }}
            className="leader_details-img"
          />

          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Box>
              <Typography
                fontSize={22}
                fontWeight={600}
                mt="10px"
                color="#11142D"
              >
                {eventDetails.activity.toUpperCase()}
              </Typography>
            </Box>
          </Stack>

          <Box mt="15px">
            <Stack
              direction="row"
              justifyContent="space-between"
              flexWrap="wrap"
              alignItems="center"
            >
              <Stack direction="row" gap={0.5} alignItems="flex-start">
                <Place
                  sx={{
                    fontSize: 18,
                    color: "#808191",
                    marginTop: 0.5,
                  }}
                />
                <Typography fontSize={16} color="#808191">
                  {eventDetails.place}
                </Typography>
              </Stack>

              <Stack direction="row" gap={0.5} alignItems="flex-start">
                <CalendarMonthOutlined
                  sx={{
                    fontSize: 18,
                    color: "#808191",
                    marginTop: 0.5,
                  }}
                />
                <Typography fontSize={16} color="#808191">
                  {eventDetails.date}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Stack mt="25px" direction="column" gap="10px">
            <Typography fontSize={18} color="#11142D">
              Description
            </Typography>
            <Typography fontSize={14} color="#808191">
              {eventDetails.description}
            </Typography>
          </Stack>
        </Box>

        <Box
          width="100%"
          flex={1}
          maxWidth={326}
          display="flex"
          flexDirection="column"
          gap="20px"
        >
          <Stack
            width="100%"
            p={2}
            direction="column"
            justifyContent="center"
            alignItems="center"
            border="1px solid #E4E4E4"
            borderRadius={2}
          >
            <Stack
              mt={2}
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <img
                src={
                  checkImage(eventDetails.creator.avatar)
                    ? eventDetails.creator.avatar
                    : "https://upload.wikimedia.org/wikipedia/commons/thumb/5/59/User-avatar.svg/2048px-User-avatar.svg.png"
                }
                alt="avatar"
                width={90}
                height={90}
                style={{
                  borderRadius: "100%",
                  objectFit: "cover",
                }}
              />

              <Box mt="15px">
                <Typography fontSize={18} fontWeight={600} color="#11142D">
                  {eventDetails.creator.name}
                </Typography>
                <Typography
                  mt="5px"
                  fontSize={14}
                  fontWeight={400}
                  color="#808191"
                >
                  Creator
                </Typography>
              </Box>

              <Typography mt={1} fontSize={16} fontWeight={600} color="#11142D">
                {eventDetails.creator.allEvents.length} Events
              </Typography>
            </Stack>

            <Stack
              width="100%"
              mt="25px"
              direction="row"
              flexWrap="wrap"
              gap={2}
            >
              <CustomButton
                title={!isCurrentUser ? "Message" : "Edit"}
                backgroundColor="#475BE8"
                color="#FCFCFC"
                fullWidth
                icon={!isCurrentUser ? <ChatBubble /> : <Edit />}
                handleClick={() => {
                  if (isCurrentUser) {
                    navigate(`/events/edit/${eventDetails._id}`);
                  }
                }}
              />
              <CustomButton
                title={!isCurrentUser ? "Call" : "Delete"}
                backgroundColor={!isCurrentUser ? "#2ED480" : "#d42e2e"}
                color="#FCFCFC"
                fullWidth
                icon={!isCurrentUser ? <Phone /> : <Delete />}
                handleClick={() => {
                  if (isCurrentUser) handleDeleteEvent();
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default EventDetails;
